import {
  AimOutlined,
  ApiOutlined,
  BellOutlined,
  FileTextOutlined,
  AuditOutlined,
  BarChartOutlined,
  BulbOutlined,
  BookOutlined,
  ClusterOutlined,
  CustomerServiceOutlined,
  DashboardOutlined,
  ExperimentOutlined,
  FolderOpenOutlined,
  HomeOutlined,
  InboxOutlined,
  LineChartOutlined,
  LockOutlined,
  MessageOutlined,
  ReadOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SettingOutlined,
  ToolOutlined,
  DeploymentUnitOutlined,
  FontSizeOutlined,
  HeartOutlined,
  StarOutlined,
  TableOutlined,
  UnorderedListOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { lazy, type ComponentType, type LazyExoticComponent, type ReactNode } from "react";

/**
 * Navigation groups, in rail order (ADR-0055). A route joins a group by its `group`; adding a page
 * (for example "Activity", "Quality" or "Health and usage" under Observe) only needs a ROUTES entry —
 * the layout renders whatever the registry holds.
 */
export interface NavGroup {
  key: GroupKey;
  /** i18n key in `shell`; omitted = no section label (Home). */
  label?: string;
  /** Collapsible section, closed until the user opens it (remembered). */
  collapsible?: boolean;
  /** i18n key of a secondary hint shown on every item of the group. */
  itemHint?: string;
}

export type GroupKey = "home" | "work" | "build" | "govern" | "observe" | "tools";

export const NAV_GROUPS: NavGroup[] = [
  { key: "home" },
  { key: "work", label: "group.work" },
  { key: "build", label: "group.build" },
  { key: "govern", label: "group.govern" },
  { key: "observe", label: "group.observe" },
  // Embedded third-party consoles (Temporal, MLflow, Grafana): advanced views for engineers.
  { key: "tools", label: "group.tools", collapsible: true, itemHint: "toolsHint" },
];

export interface AppRoute {
  key: string;
  path: string;
  /** i18n key in the `shell` namespace. */
  label: string;
  icon: ReactNode;
  /** Any of these realm roles; empty = every signed-in user. */
  roles: string[];
  group: GroupKey;
  /** false = reachable (palette, help, links) but not listed in the rail. */
  nav?: boolean;
  /** Glossary ids shown as "Key terms" under the page header. */
  terms?: string[];
  /** User-guide chapter (docs/user-guide/<lang>/<chapter>.md) for the Help drawer. */
  chapter: string;
  Component: LazyExoticComponent<ComponentType>;
  /** Reachable by link only (no navigation item), e.g. the request page. */
  hidden?: boolean;
}

const Home = lazy(() => import("./features/home/HomePage"));
const AgentOverview = lazy(() => import("./features/agents/AgentOverviewPage"));
const HowItWorks = lazy(() => import("./features/howItWorks/HowItWorksPage"));
const Glossary = lazy(() => import("./features/glossary/GlossaryPage"));
const DataLevels = lazy(() => import("./features/dataLevels/DataLevelsPage"));
const Guide = lazy(() => import("./features/guide/GuidePage"));
const Chat = lazy(() => import("./features/chat/ChatPage"));
const Inbox = lazy(() => import("./features/inbox/InboxPage"));
const Cases = lazy(() => import("./features/cases/CasesPage"));
const Builder = lazy(() => import("./features/builder/BuilderPage"));
const Governance = lazy(() => import("./features/governance/GovernancePage"));
const Operator = lazy(() => import("./features/operator/OperatorPage"));
const Executive = lazy(() => import("./features/executive/ExecutivePage"));
const Models = lazy(() => import("./features/models/ModelsPage"));
const Temporal = lazy(() => import("./features/embedded/TemporalPage"));
const Mlflow = lazy(() => import("./features/embedded/MlflowPage"));
const Grafana = lazy(() => import("./features/embedded/GrafanaPage"));
const Privacy = lazy(() => import("./features/privacy/PrivacyPage"));
const Knowledge = lazy(() => import("./features/knowledge/KnowledgePage"));
const MyMemory = lazy(() => import("./features/memory/MyMemoryPage"));
const DocumentReader = lazy(() => import("./features/knowledge/DocumentReader"));
const Admin = lazy(() => import("./features/admin/AdminPage"));
const Channels = lazy(() => import("./features/channels/ChannelsPage"));
const Requests = lazy(() => import("./features/requests/RequestPage"));
const Activity = lazy(() => import("./features/observe/ActivityPage"));
const Command = lazy(() => import("./features/command/CommandPage"));
const Health = lazy(() => import("./features/observe/HealthPage"));
const Quality = lazy(() => import("./features/observe/QualityPage"));
const Notifications = lazy(() => import("./features/work/NotificationsPage"));
const DataTables = lazy(() => import("./features/dataTables/DataTablesPage"));
const BulkRuns = lazy(() => import("./features/bulkRuns/BulkRunsPage"));
const MadeFiles = lazy(() => import("./features/work/DocumentPage"));
const RecordWorkspace = lazy(() => import("./features/officer/RecordWorkspacePage"));
/** Health and usage: operations, finance, administration and executives (control plane HEALTH_ROLES). */
export const HEALTH_ROLES = ["operator", "finance", "tenant-admin", "platform-admin", "platform-auditor", "executive"];
/** Quality: people who build, release, run or audit agents and models (control plane QUALITY_ROLES). */
export const QUALITY_ROLES = ["builder", "agent-admin", "model-admin", "release-approver", "operator", "tenant-admin", "auditor", "platform-auditor", "platform-admin"];

/** Roles that see Governance; each tab narrows further (see GovernancePage). */
export const GOVERNANCE_ROLES = [
  "agent-admin",
  "auditor",
  "platform-auditor",
  "release-approver",
  "kill-switch-operator",
  "tenant-admin",
  "builder",
  "platform-admin",
  "tool-admin",
  "marketplace-curator",
  // ADR-0077: writes the organisation's own rules (Governance › Rules).
  "rules-admin",
];

export const ROUTES: AppRoute[] = [
  { key: "home", path: "/home", label: "nav.home", icon: <HomeOutlined />, roles: [], group: "home", chapter: "01-getting-started", Component: Home },
  { key: "chat", path: "/chat", label: "nav.chat", icon: <MessageOutlined />, roles: [], group: "work", terms: ["agent", "run"], chapter: "03-assistant", Component: Chat },
  { key: "inbox", path: "/inbox", label: "nav.inbox", icon: <InboxOutlined />, roles: ["reviewer", "approver", "finance-approver"], group: "work", terms: ["task", "approver", "sla"], chapter: "04-reviewer-inbox", Component: Inbox },
  { key: "cases", path: "/cases", label: "nav.cases", icon: <FolderOpenOutlined />, roles: ["case-worker", "case-supervisor", "case-admin", "auditor", "tenant-admin"], group: "work", terms: ["case", "classification", "sla"], chapter: "05-cases", Component: Cases },
  { key: "channels", path: "/channels", label: "nav.channels", icon: <CustomerServiceOutlined />, roles: ["channel-agent", "channel-supervisor", "channel-admin", "channel-consent-admin", "platform-admin"], group: "work", terms: ["channel", "handover", "consent"], chapter: "13-channels", Component: Channels },
  { key: "builder", path: "/builder", label: "nav.builder", icon: <ToolOutlined />, roles: ["builder", "agent-admin"], group: "build", terms: ["agent", "prompt", "riskTier", "evaluation"], chapter: "06-builder", Component: Builder },
  // One agent in one place: where it is reachable, models, tools, rules; each line links to where it changes.
  { key: "agentOverview", path: "/agents", label: "agentOverview:title", icon: <DeploymentUnitOutlined />, roles: ["builder", "agent-admin", "tenant-admin", "platform-admin", "channel-admin", "model-admin", "tool-admin", "release-approver", "auditor", "platform-auditor", "operator"], group: "build", nav: false, terms: ["agent", "alias", "channel"], chapter: "06-builder", Component: AgentOverview },
  { key: "knowledge", path: "/knowledge", label: "nav.knowledge", icon: <BookOutlined />, roles: ["retrieval-curator", "builder"], group: "build", terms: ["collection", "classification"], chapter: "06-builder", Component: Knowledge },
  // Small organisation tables agents read and keep, and people edit (ADR-0106).
  { key: "dataTables", path: "/data-tables", label: "nav.dataTables", icon: <TableOutlined />, roles: ["builder", "agent-admin", "case-supervisor", "tenant-admin", "auditor"], group: "build", terms: ["agent"], chapter: "06-builder", Component: DataTables },
  // One agent over many rows: progress, per-row results, retry, cost limit, export (ADR-0107).
  { key: "bulkRuns", path: "/bulk-runs", label: "nav.bulkRuns", icon: <UnorderedListOutlined />, roles: ["builder", "agent-admin", "operator", "auditor"], group: "build", terms: ["agent", "run", "budget"], chapter: "06-builder", Component: BulkRuns },
  { key: "knowledgeRead", path: "/knowledge/read", label: "reader:title", icon: <BookOutlined />, roles: [], group: "build", nav: false, hidden: true, terms: ["collection", "classification"], chapter: "06-builder", Component: DocumentReader },
  { key: "models", path: "/models", label: "nav.models", icon: <ApiOutlined />, roles: ["model-admin"], group: "build", terms: ["alias", "fallback", "route", "budget"], chapter: "08-models", Component: Models },
  { key: "governance", path: "/governance", label: "nav.governance", icon: <SafetyCertificateOutlined />, roles: GOVERNANCE_ROLES, group: "govern", terms: ["promotion", "killSwitch", "policy", "agentIdentity", "riskTier"], chapter: "07-governance", Component: Governance },
  { key: "privacy", path: "/privacy", label: "nav.privacy", icon: <LockOutlined />, roles: ["privacy-officer", "guardrails-admin"], group: "govern", terms: ["pii", "dataSubject", "guardrails"], chapter: "11-audit-privacy", Component: Privacy },
  { key: "admin", path: "/admin", label: "nav.admin", icon: <SettingOutlined />, roles: ["platform-admin", "tool-admin", "tenant-admin", "operator", "finance"], group: "govern", terms: ["tenant", "budget"], chapter: "12-platform-admin", Component: Admin },
  { key: "activity", path: "/activity", label: "nav.activity", icon: <ThunderboltOutlined />, roles: [], group: "observe", terms: ["run", "approver"], chapter: "09-operations", Component: Activity },
  // ADR-0112: agents at work, live or replayed, and the Galaxy of all agents. Everyone who can use
  // the Assistant or see Activity (both: every signed-in person); each part re-checks access.
  { key: "command", path: "/command", label: "nav.command", icon: <AimOutlined />, roles: [], group: "observe", terms: ["agent", "run"], chapter: "09-operations", Component: Command },
  { key: "health", path: "/health", label: "nav.health", icon: <HeartOutlined />, roles: HEALTH_ROLES, group: "observe", terms: ["budget", "usage"], chapter: "09-operations", Component: Health },
  { key: "quality", path: "/quality", label: "nav.quality", icon: <StarOutlined />, roles: QUALITY_ROLES, group: "observe", terms: ["evaluation", "environment"], chapter: "09-operations", Component: Quality },
  // Old address of Operations: redirects to Health and usage or Administration › Spending limits.
  { key: "operator", path: "/operator", label: "nav.operator", icon: <DashboardOutlined />, roles: ["operator", "finance", "tenant-admin"], group: "observe", nav: false, hidden: true, terms: ["budget", "canary", "usage"], chapter: "09-operations", Component: Operator },
  { key: "executive", path: "/executive", label: "nav.executive", icon: <BarChartOutlined />, roles: ["executive", "finance"], group: "observe", terms: ["usage"], chapter: "10-executive", Component: Executive },
  { key: "temporal", path: "/observability/temporal", label: "nav.temporal", icon: <ClusterOutlined />, roles: ["operator", "agent-admin"], group: "tools", terms: ["workflow"], chapter: "09-operations", Component: Temporal },
  { key: "mlflow", path: "/observability/mlflow", label: "nav.mlflow", icon: <ExperimentOutlined />, roles: ["builder", "model-admin", "agent-admin"], group: "tools", terms: ["evaluation"], chapter: "09-operations", Component: Mlflow },
  { key: "grafana", path: "/observability/grafana", label: "nav.grafana", icon: <LineChartOutlined />, roles: ["operator", "platform-auditor"], group: "tools", chapter: "09-operations", Component: Grafana },
  { key: "howItWorks", path: "/how-it-works", label: "nav.howItWorks", icon: <DeploymentUnitOutlined />, roles: [], group: "home", nav: false, terms: ["conductor", "auditLedger"], chapter: "02-concepts", Component: HowItWorks },
  { key: "dataLevels", path: "/data-levels", label: "nav.dataLevels", icon: <SafetyOutlined />, roles: [], group: "home", nav: false, terms: ["classification"], chapter: "02-concepts", Component: DataLevels },
  { key: "glossary", path: "/glossary", label: "nav.glossary", icon: <FontSizeOutlined />, roles: [], group: "home", nav: false, chapter: "15-glossary", Component: Glossary },
  { key: "guide", path: "/guide", label: "nav.guide", icon: <ReadOutlined />, roles: [], group: "home", nav: false, chapter: "01-getting-started", Component: Guide },
  // ADR-0057: what happened in one request (/requests/:runId); every service re-checks access
  // (OPA explain.read). Reachable by link only.
  { key: "requests", path: "/requests", label: "requests:pageTitle", icon: <AuditOutlined />, roles: [], group: "work", nav: false, hidden: true, terms: ["run", "auditLedger"], chapter: "01-getting-started", Component: Requests },
  // ADR-0091: what agents told the person or their teams (the bell), and files agents made
  // (/documents/:id, opened from links in chats, notifications and e-mails).
  { key: "notifications", path: "/notifications", label: "work:notifications.title", icon: <BellOutlined />, roles: [], group: "work", nav: false, terms: ["agent"], chapter: "03-assistant", Component: Notifications },
  { key: "madeFiles", path: "/documents", label: "work:file.title", icon: <FileTextOutlined />, roles: [], group: "work", nav: false, hidden: true, terms: ["classification"], chapter: "03-assistant", Component: MadeFiles },
  // ADR-0118: one record on one page (/records/:type/:key), as the person's duty profile
  // describes it; every lookup and analysis is an agent request. Reachable by link only.
  { key: "records", path: "/records", label: "officer:workspace.title", icon: <FolderOpenOutlined />, roles: [], group: "work", nav: false, hidden: true, terms: ["agent", "run"], chapter: "09-operations", Component: RecordWorkspace },
  { key: "myMemory", path: "/me/memory", label: "myMemory:title", icon: <BulbOutlined />, roles: [], group: "home", nav: false, hidden: true, terms: ["memory", "consent"], chapter: "03-assistant", Component: MyMemory },
];

export function routesFor(roles: readonly string[]): AppRoute[] {
  const set = new Set(roles);
  return ROUTES.filter((r) => !r.hidden && (r.roles.length === 0 || r.roles.some((x) => set.has(x))));
}

/** Routes listed in the rail, grouped in NAV_GROUPS order; empty groups are dropped. */
export function navFor(roles: readonly string[]): { group: NavGroup; routes: AppRoute[] }[] {
  const mine = routesFor(roles).filter((r) => r.nav !== false);
  return NAV_GROUPS.map((group) => ({ group, routes: mine.filter((r) => r.group === group.key) })).filter((g) => g.routes.length > 0);
}

/** The route that owns a pathname (longest matching prefix). */
export function routeOf(pathname: string): AppRoute | undefined {
  return [...ROUTES]
    .sort((a, b) => b.path.length - a.path.length)
    .find((r) => pathname === r.path || pathname.startsWith(`${r.path}/`));
}
